import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Banknote,
  CreditCard,
  QrCode,
  Wallet,
  Loader2,
  RefreshCw,
  Lock,
  Undo2,
  Hourglass,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ShiftSummaryCard } from '@/components/pos/ShiftSummaryCard';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { EmptyState } from '@/components/pos/EmptyState';
import { OpenShiftPanel } from '@/components/pos/OpenShiftPanel';
import {
  useShiftSummary,
  useCurrentShift,
} from '@/hooks/queries';
import { todayISO, formatDate, rupiah, number } from '@/lib/format';
import { expectedDrawerCash, shiftTenderRows, shiftTenderTotals } from '@/lib/shiftTenders';
import type { ShiftAdjustments } from '@/types/sale';

/** Tile icon per tender. An unmapped tender still renders — with a wallet. */
const TENDER_ICON: Record<string, LucideIcon> = {
  cash: Banknote,
  'e-money': CreditCard,
  midtrans: QrCode,
  'edc-permata': CreditCard,
};

export function ShiftSummaryPage() {
  const today = todayISO();
  const { data, isLoading, isFetching, isError, refetch } = useShiftSummary(today);
  const { data: current, isLoading: shiftLoading } = useCurrentShift();

  // Every tender the API returned — not a hardcoded three. See
  // `shiftTenderRows` for what hiding one costs a cashier.
  const tenders = useMemo(() => shiftTenderRows(data?.byMethod), [data?.byMethod]);
  const { gross: grandGross, count: grandCount } = useMemo(
    () => shiftTenderTotals(tenders),
    [tenders],
  );

  // While a shift is open, the drawer should also hold the opening float.
  const openingFloat = current?.openingFloat ?? 0;
  const expectedCash = expectedDrawerCash(tenders, openingFloat);

  return (
    <div className="mx-auto h-full max-w-4xl overflow-y-auto px-6 py-8 pos-scroll">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-900">Ringkasan Shift</h1>
          <p className="text-sm text-muted-foreground">{formatDate(today)}</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Segarkan
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20 text-ink-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : !data ? (
        <EmptyState
          icon={Wallet}
          title="Ringkasan belum tersedia"
          description={
            isError
              ? 'Gagal memuat ringkasan shift. Coba segarkan.'
              : 'Endpoint ringkasan shift belum aktif. Hubungi administrator.'
          }
          action={
            <Button variant="outline" onClick={() => refetch()}>
              Coba lagi
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {tenders.map((t) => (
              <ShiftSummaryCard
                key={t.key}
                label={t.label}
                icon={TENDER_ICON[t.key] ?? Wallet}
                count={t.count}
                gross={t.gross}
                discount={t.discount}
                accent={t.key === 'cash' ? 'crimson' : 'neutral'}
              />
            ))}
          </div>

          <Card>
            <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Total bruto hari ini</p>
                <MoneyValue value={grandGross} emphasis className="text-4xl" />
                <p className="tnum mt-1 text-sm text-ink-400">{grandCount} transaksi</p>
              </div>
              <div className="rounded-xl border-2 border-gold-500/40 bg-gold-200/40 px-6 py-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">
                  Kas tunai diharapkan
                </p>
                <MoneyValue value={expectedCash} emphasis className="text-2xl" />
                <p className="mt-1 text-xs text-ink-400">
                  {openingFloat > 0 ? `Modal ${rupiah(openingFloat)} + tunai` : 'Untuk rekonsiliasi laci kas'}
                </p>
              </div>
            </CardContent>
          </Card>

          {data.adjustments && <AdjustmentsCard adj={data.adjustments} />}

          {/* Till session — open, or count + close */}
          {shiftLoading ? (
            <div className="flex justify-center py-4 text-ink-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !current ? (
            <Card>
              <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                  <p className="font-display text-base font-semibold text-ink-900">Belum ada shift berjalan</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Buka shift untuk mulai merekonsiliasi laci kas. Modal awal diatur oleh Finance.
                  </p>
                </div>
                {/* The float is set by Finance in the back office — shown, never typed. */}
                <OpenShiftPanel className="w-full sm:w-72" />
              </CardContent>
            </Card>
          ) : (
            // Counting the drawer and closing live on their own page (/shift/close).
            <Button asChild className="w-full gap-2">
              <Link to="/shift/close">
                <Lock className="h-4 w-4" aria-hidden="true" /> Tutup shift
              </Link>
            </Button>
          )}

          <Button variant="outline" className="w-full gap-2" onClick={() => window.print()}>
            Cetak Laporan Shift
          </Button>
        </div>
      )}
    </div>
  );
}

/** Void / refund recap for the day (F.01) — rendered only when the BE sends it. */
function AdjustmentsCard({ adj }: { adj: ShiftAdjustments }) {
  const voidCount = Number(adj.voidCount) || 0;
  const voidAmount = Number(adj.voidAmount) || 0;
  const refundCount = Number(adj.refundCount) || 0;
  const refundAmount = Number(adj.refundAmount) || 0;
  const pending = Number(adj.pendingRequests) || 0;
  return (
    <Card data-testid="shift-adjustments">
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-center gap-2">
          <Undo2 className="h-5 w-5 text-primary" />
          <p className="font-display text-base font-semibold text-ink-900">Void &amp; Refund</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <AdjStat label="Void" count={voidCount} amount={voidAmount} />
          <AdjStat label="Refund" count={refundCount} amount={refundAmount} />
          <div className="rounded-lg border border-cream-200 bg-white px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Permintaan menunggu</p>
            <p className="tnum font-display text-2xl font-bold text-ink-900">{number(pending)}</p>
          </div>
        </div>
        {pending > 0 && (
          <div className="flex items-start gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Hourglass className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Masih ada {number(pending)} permintaan void/refund menunggu persetujuan. Shift tetap bisa
              ditutup — laporkan ke supervisor.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdjStat({ label, count, amount }: { label: string; count: number; amount: number }) {
  return (
    <div className="rounded-lg border border-cream-200 bg-white px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">{label}</p>
      <MoneyValue value={amount} emphasis className="text-xl" />
      <p className="tnum text-xs text-ink-400">{number(count)} transaksi</p>
    </div>
  );
}
